import {Injectable, OnDestroy, Renderer2, RendererFactory2} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription, takeUntil} from "rxjs";
import {Melody} from "../model/melody";
import {AudioState} from "../model/audio-state";
import {SubscriberMelody} from "../model/subscriber-melody";

@Injectable({
  providedIn: 'root'
})
export class AudioService implements OnDestroy {
  private readonly audio: any;
  private stop$ = new Subject<void>();
  private activeMelody: Melody | SubscriberMelody;

  private subscription = new Subscription();

  private audioEvents = [
    "ended",
    "error",
    "play",
    "playing",
    "pause",
    "timeupdate",
    "canplay",
    "loadedmetadata",
    "loadstart"
  ];

  private state = new AudioState();
  private stateChange: BehaviorSubject<AudioState> = new BehaviorSubject(this.state);

  constructor(rendererFactory: RendererFactory2) {
    const renderer: Renderer2 = rendererFactory.createRenderer(null, null);
    const playerContainerElement = renderer.createElement('audio');
    playerContainerElement.id = 'audioPlayer';
    playerContainerElement.src = '';
    playerContainerElement.preload = 'none';
    renderer.appendChild(document.body, playerContainerElement);
    this.audio = document.getElementById('audioPlayer');
  }

  private streamObservable(url: string): any {
    return new Observable(observer => {
      this.audio.src = url;
      this.audio.load();
      this.audio.play();

      const handler = (event: Event) => {
        this.updateStateEvents(event);
        observer.next(event);
      };

      this.addEvents(this.audio, this.audioEvents, handler);

      return () => {
        this.removeEvents(this.audio, this.audioEvents, handler);
        this.resetState();
        this.audio.pause();
        this.audio.currentTime = 0;
        this.state.currentTime = 0;
      };
    });
  }

  private addEvents(obj: { addEventListener: (arg0: any, arg1: any) => void; }, events: any[], handler: (event: Event) => void) {
    events.forEach(event => {
      obj.addEventListener(event, handler);
    });
  }

  private removeEvents(obj: { removeEventListener: (arg0: any, arg1: any) => void; }, events: any[], handler: (event: Event) => void) {
    events.forEach(event => {
      obj.removeEventListener(event, handler);
    });
  }

  private updateStateEvents(event: Event): void {
    switch (event.type) {
      case "canplay":
        this.state.duration = this.audio.duration;
        this.state.canPlay = true;
        break;
      case "playing":
        this.state.isPlaying = true;
        break;
      case "pause":
        this.state.isPlaying = false;
        break;
      case "timeupdate":
        this.state.currentTime = this.audio.currentTime;
        break;
      case "ended":
        this.state.ended = true;
        this.state.currentTime = 0;
        break;
      case "error":
        this.resetState();
        this.state.error = true;
        break;
    }
    this.stateChange.next(this.state);
  }

  private resetState() {
    this.state = new AudioState();
    this.stateChange.next(this.state);
  }

  private resetCurrentTime(): void {
    this.state.currentTime = null;
    this.state.duration = null;
    this.stateChange.next(this.state);
  }

  getState(): Observable<AudioState> {
    return this.stateChange.asObservable();
  }

  private stop(): void {
    this.stop$.next();
  }

  private playStream(url: string) {
    return this.streamObservable(url).pipe(takeUntil(this.stop$));
  }

  private setActiveMelody(melody: Melody | SubscriberMelody): void {
    this.activeMelody = melody;
  }

  togglePlay(melody: Melody | SubscriberMelody): void {
    if (melody.isPlaying) {
      this.stopTrack();
    } else {
      this.playTrack(melody);
    }
  }

  playTrack(melody: Melody | SubscriberMelody): void {
    if (this.activeMelody) {
      this.activeMelody.isPlaying = false;
    }
    melody.isPlaying = true;
    this.resetCurrentTime();
    this.setActiveMelody(melody);
    this.subscription.add(
      this.playStream(melody.mp3Path)
        .pipe(
          takeUntil(this.stop$)
        )
        .subscribe((stream: { type: string; }) => {
          if (stream.type == 'ended') {
            this.stop();
            melody.isPlaying = false;
            this.setActiveMelody(null);
          }
        })
    );
  }

  stopTrack(): void {
    if (this.state.isPlaying) {
      this.stop();
      this.activeMelody.isPlaying = false;
      this.resetCurrentTime();
      this.setActiveMelody(null);
    }
  }

  pauseTrack(): void {
    if (this.state.isPlaying) {
      this.audio.pause();
      this.activeMelody.isPlaying = false;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
